import { Controller } from "@hotwired/stimulus"

// The TREND Files picker: one sheet, opened by either composer through a
// document event ("files-picker:open", detail.owner names who asked) and
// answered with "files-picker:chosen" carrying the rows and the owner.
// Search only, through FilesController#search (JSON), which applies the
// one visibility scope; a row that cannot go by text is disabled with its
// reason. Escape and the backdrop close it.
export default class extends Controller {
  static targets = ["backdrop", "query", "results", "attach"]
  static values = { url: String }

  connect() {
    this.chosen = new Map()
    this.rows = []
    this.onOpen = (e) => this.open(e.detail || {})
    document.addEventListener("files-picker:open", this.onOpen)
  }

  disconnect() { document.removeEventListener("files-picker:open", this.onOpen) }

  open({ owner = "", opener = null } = {}) {
    this.owner = owner
    this.opener = opener
    this.chosen.clear()
    this.queryTarget.value = ""
    this.resultsTarget.innerHTML = `<li class="cw-results-empty">Type a file name.</li>`
    this.element.hidden = false
    this.renderFoot()
    requestAnimationFrame(() => this.queryTarget.focus())
  }

  close() {
    this.element.hidden = true
    if (this.opener && this.opener.focus) this.opener.focus()
  }

  backdrop(event) { if (event.target === this.backdropTarget) this.close() }

  keydown(event) {
    if (event.key === "Escape") { event.stopPropagation(); this.close() }
  }

  search() {
    clearTimeout(this.timer)
    const q = this.queryTarget.value.trim()
    if (q.length < 2) { this.resultsTarget.innerHTML = `<li class="cw-results-empty">Type a file name.</li>`; return }
    this.timer = setTimeout(async () => {
      try {
        const res = await fetch(`${this.urlValue}?q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } })
        if (!res.ok) return
        const data = await res.json()
        if (this.queryTarget.value.trim() !== q) return
        this.rows = data.rows || []
        this.renderRows()
      } catch (_e) { /* the list stays as it was */ }
    }, 200)
  }

  renderRows() {
    if (this.rows.length === 0) { this.resultsTarget.innerHTML = `<li class="cw-results-empty">No file named that in TREND Files.</li>`; return }
    this.resultsTarget.innerHTML = this.rows.map((r) => `
      <li role="option" aria-selected="${this.chosen.has(r.id)}">
        <button type="button" class="cw-row fp-row ${this.chosen.has(r.id) ? "is-chosen" : ""} ${r.textable ? "" : "is-refused"}" data-id="${r.id}" data-action="files-picker#toggle" ${r.textable ? "" : "disabled"}>
          <span class="fp-check" aria-hidden="true">${this.chosen.has(r.id) ? "&#10003;" : ""}</span>
          <span class="cw-row-text">
            <span class="cw-row-name">${this.esc(r.name)}</span>
            <span class="cw-row-detail ${r.textable ? "" : "is-refused"}">${this.esc(r.textable ? `${r.path} · ${r.human_size}` : r.reason)}</span>
          </span>
        </button>
      </li>`).join("")
  }

  toggle(event) {
    const id = Number(event.currentTarget.dataset.id)
    const row = this.rows.find((r) => r.id === id)
    if (!row) return
    if (this.chosen.has(id)) this.chosen.delete(id)
    else this.chosen.set(id, row)
    this.renderRows()
    this.renderFoot()
  }

  renderFoot() {
    const n = this.chosen.size
    this.attachTarget.disabled = n === 0
    this.attachTarget.textContent = n > 1 ? `Attach ${n} files` : "Attach"
  }

  attach() {
    const rows = [...this.chosen.values()]
    document.dispatchEvent(new CustomEvent("files-picker:chosen", { detail: { owner: this.owner, rows } }))
    this.close()
  }

  esc(s) { const d = document.createElement("div"); d.textContent = s == null ? "" : String(s); return d.innerHTML.replace(/"/g, "&quot;") }
}
